import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import { supabase } from '@/lib/supabaseClient';

export default function RegisterPage() {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);

  const isValid = email.trim() !== '' && password.length >= 6;

  const DEFAULT_TENANT_ID = 'a1b2c3d4-e5f6-7890-abcd-ef1234567890';

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid) {
      Swal.fire('Campos requeridos', 'Email válido y contraseña de mínimo 6 caracteres.', 'warning');
      return;
    }
    setLoading(true);

    const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
      email: email.trim(),
      password,
      options: name.trim() ? { data: { full_name: name.trim() } } : undefined,
    });

    if (signUpError) {
      setLoading(false);
      Swal.fire('Error', signUpError.message, 'error');
      return;
    }

    // Vincular usuario al tenant por defecto
    if (signUpData?.user) {
      const { error: tenantError } = await supabase
        .from('saas_tenant_users')
        .insert({
          user_id: signUpData.user.id,
          tenant_id: DEFAULT_TENANT_ID,
          role: 'owner',
        });

      if (tenantError) {
        console.warn('[Register] Error linking tenant:', tenantError.message);
      } else {
        console.log('[Register] Tenant linked successfully. tenant_id:', DEFAULT_TENANT_ID);
      }
    }

    setLoading(false);
    await Swal.fire('¡Cuenta creada!', 'Revisa tu email para confirmar tu cuenta.', 'success');
    navigate('/login');
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-neutral-950 relative overflow-hidden"
      style={{ fontFamily: '\'Inter\', sans-serif' }}
    >
      {/* Background ambient */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute top-[-120px] left-[-120px] w-[500px] h-[500px] rounded-full opacity-10"
          style={{ background: 'radial-gradient(circle, #f59e0b 0%, transparent 70%)' }}
        />
        <div
          className="absolute bottom-[-100px] right-[-100px] w-[420px] h-[420px] rounded-full opacity-8"
          style={{ background: 'radial-gradient(circle, #d97706 0%, transparent 70%)' }}
        />
      </div>

      <div className="relative z-10 w-full max-w-md mx-4">
        {/* Logo + Brand */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 mb-3">
            <div className="w-9 h-9 flex items-center justify-center bg-amber-500 rounded-lg">
              <i className="ri-camera-3-line text-neutral-950 text-lg" />
            </div>
            <span className="text-white text-2xl font-bold tracking-tight">
              Pixory <span className="text-amber-400">AI</span>
            </span>
          </div>
          <p className="text-neutral-500 text-sm">Crea tu cuenta gratuita</p>
        </div>

        {/* Card */}
        <form
          onSubmit={handleRegister}
          className="bg-neutral-900 border border-neutral-800 rounded-2xl p-8 flex flex-col gap-5"
        >
          <div>
            <h1 className="text-white text-xl font-semibold mb-1">Crear cuenta</h1>
            <p className="text-neutral-500 text-sm">Empieza a gestionar tu estudio hoy</p>
          </div>

          {/* Name (optional) */}
          <div className="flex flex-col gap-1.5">
            <label className="text-neutral-400 text-xs font-medium uppercase tracking-wider">
              Nombre <span className="text-neutral-600 normal-case tracking-normal">(opcional)</span>
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <i className="ri-user-3-line text-neutral-500 text-sm" />
              </div>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Tu nombre"
                autoComplete="name"
                className="w-full bg-neutral-800 border border-neutral-700 text-white text-sm placeholder-neutral-600 rounded-lg pl-9 pr-4 py-3 focus:outline-none focus:border-amber-500 transition-colors"
              />
            </div>
          </div>

          {/* Email */}
          <div className="flex flex-col gap-1.5">
            <label className="text-neutral-400 text-xs font-medium uppercase tracking-wider">
              Correo electrónico
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <i className="ri-mail-line text-neutral-500 text-sm" />
              </div>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tu@email.com"
                autoComplete="email"
                className="w-full bg-neutral-800 border border-neutral-700 text-white text-sm placeholder-neutral-600 rounded-lg pl-9 pr-4 py-3 focus:outline-none focus:border-amber-500 transition-colors"
              />
            </div>
          </div>

          {/* Password */}
          <div className="flex flex-col gap-1.5">
            <label className="text-neutral-400 text-xs font-medium uppercase tracking-wider">
              Contraseña
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <i className="ri-lock-line text-neutral-500 text-sm" />
              </div>
              <input
                type={showPass ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Mínimo 6 caracteres"
                autoComplete="new-password"
                className="w-full bg-neutral-800 border border-neutral-700 text-white text-sm placeholder-neutral-600 rounded-lg pl-9 pr-10 py-3 focus:outline-none focus:border-amber-500 transition-colors"
              />
              <button
                type="button"
                onClick={() => setShowPass(!showPass)}
                className="absolute inset-y-0 right-0 flex items-center pr-3 cursor-pointer text-neutral-500 hover:text-neutral-300 transition-colors"
              >
                <i className={showPass ? 'ri-eye-off-line text-sm' : 'ri-eye-line text-sm'} />
              </button>
            </div>
            {password.length > 0 && password.length < 6 && (
              <p className="text-red-400 text-xs">La contraseña debe tener al menos 6 caracteres</p>
            )}
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={loading || !isValid}
            className="w-full bg-amber-500 hover:bg-amber-400 disabled:bg-amber-500/40 disabled:cursor-not-allowed text-neutral-950 font-semibold text-sm rounded-lg py-3 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 mt-1"
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-neutral-950 border-t-transparent rounded-full animate-spin" />
                Creando cuenta...
              </>
            ) : (
              <>
                <i className="ri-user-add-line text-base" />
                Crear cuenta
              </>
            )}
          </button>

          {/* Link to login */}
          <p className="text-center text-neutral-500 text-sm">
            ¿Ya tenés una cuenta?{' '}
            <Link to="/login" className="text-amber-400 hover:text-amber-300 transition-colors font-medium">
              Iniciar sesión
            </Link>
          </p>
        </form>

        <p className="text-center text-neutral-700 text-xs mt-6">
          © 2026 Pixory AI · Todos los derechos reservados
        </p>
      </div>
    </div>
  );
}
